import React, { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import Layout from "../components/Layout";
import api from "../api";

// Player Lists -- named, standalone lists of people (name + handicap),
// independent of the live players roster and of any specific competition.
// Built for Mike's own stated need: a club running a one-off Open doesn't
// have "the membership" a society does, just whichever named list is
// relevant (e.g. "Senior's Open entrants"), reusable across competitions.
// "Recall" adds a list's people into the live roster -- it never touches
// any event, unlike the unrelated Saved Games feature (which snapshots a
// whole event setup, not a standalone list of people).
export default function PlayerLists() {
  const [lists, setLists] = useState([]);
  const [selected, setSelected] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [newEntryName, setNewEntryName] = useState("");
  const [newEntryHcp, setNewEntryHcp] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [toast, setToast] = useState("");
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const [recalling, setRecalling] = useState(false);
  const [savingGroup, setSavingGroup] = useState(false);
  const [saveGroupName, setSaveGroupName] = useState("");
  const [importingNew, setImportingNew] = useState(false);
  const [importNewError, setImportNewError] = useState("");
  const fileInputRef = useRef(null);
  const fileInputNewRef = useRef(null);
  // Native window.confirm/window.prompt dialogs can get stuck on some
  // setups (hit this live, both in testing and on Mike's own machine --
  // the whole page goes unresponsive, nothing clickable) -- every
  // confirmation and name prompt on this page uses GSM's own in-page modal
  // style instead, same pattern already proven elsewhere (e.g. the delete-
  // fixture modal in PlannerEventDetail.jsx).
  const [confirmDeleteList, setConfirmDeleteList] = useState(null);
  const [confirmLoadGroup, setConfirmLoadGroup] = useState(false);
  const [pendingImport, setPendingImport] = useState(null); // {parsedPlayers, defaultName}
  const [pendingImportName, setPendingImportName] = useState("");

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3500); };

  useEffect(() => { loadLists(); }, []);

  const loadLists = async () => {
    try {
      const res = await api.get("/player-lists");
      setLists(res.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openList = async (list) => {
    setSelected(list);
    setEntries([]);
    setImportError("");
    setLoadingEntries(true);
    try {
      const res = await api.get(`/player-lists/${list.id}`);
      setEntries(res.data.entries);
    } catch(e) { showToast("Couldn't load that list"); }
    finally { setLoadingEntries(false); }
  };

  const createList = async () => {
    if (!newListName.trim()) return;
    try {
      const res = await api.post("/player-lists", { name: newListName.trim() });
      setNewListName("");
      await loadLists();
      openList(res.data);
    } catch(e) { showToast("Couldn't create list"); }
  };

  const startRename = () => { setRenameValue(selected.name); setRenaming(true); };
  const saveRename = async () => {
    if (!renameValue.trim()) { setRenaming(false); return; }
    try {
      const res = await api.put(`/player-lists/${selected.id}`, { name: renameValue.trim() });
      setSelected(res.data);
      setRenaming(false);
      loadLists();
    } catch(e) { showToast("Couldn't rename list"); }
  };

  const deleteList = (list) => setConfirmDeleteList(list);
  const confirmDelete = async () => {
    const list = confirmDeleteList;
    setConfirmDeleteList(null);
    try {
      await api.delete(`/player-lists/${list.id}`);
      if (selected?.id === list.id) { setSelected(null); setEntries([]); }
      loadLists();
      showToast("List deleted");
    } catch(e) { showToast("Couldn't delete list"); }
  };

  const addEntry = async () => {
    if (!newEntryName.trim()) return;
    try {
      const res = await api.post(`/player-lists/${selected.id}/entries`, { name: newEntryName.trim(), handicap: parseInt(newEntryHcp, 10) || 0 });
      setEntries(prev => [...prev, res.data].sort((a,b) => (a.name||"").localeCompare(b.name||"")));
      setNewEntryName(""); setNewEntryHcp("");
      loadLists();
    } catch(e) { showToast("Couldn't add " + newEntryName); }
  };

  // A single-row remove is low-stakes and easily re-added, so this
  // deliberately doesn't block with a confirm modal -- but it MUST give
  // visible feedback. Without it, this click is indistinguishable from a
  // broken button: nothing on screen told you anything happened, so
  // repeated frustrated clicking silently emptied a real list one name at
  // a time with zero indication it was actually working. Found live.
  const removeEntry = async (entry) => {
    try {
      await api.delete(`/player-lists/${selected.id}/entries/${entry.id}`);
      setEntries(prev => prev.filter(e => e.id !== entry.id));
      loadLists();
      showToast(`Removed ${entry.name}`);
    } catch(e) { showToast("Couldn't remove " + entry.name); }
  };

  // Primary, first-use entry point: upload a spreadsheet straight into a
  // brand-new named group in one action -- no "create an empty list first"
  // step. Asks for the group's name only after a valid file is parsed, so
  // picking a bad file doesn't needlessly interrupt with a name prompt.
  const pickImportNewFile = () => fileInputNewRef.current?.click();
  const handleImportNewFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImportNewError(""); setImportingNew(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "" });
      if (!rows.length) { setImportNewError("That file has no rows to import."); return; }
      const parseRes = await api.post("/players/excel-parse", { rows });
      const parsedPlayers = parseRes.data.players || [];
      if (!parsedPlayers.length) { setImportNewError("Couldn't find any players in that spreadsheet."); return; }
      const defaultName = file.name.replace(/\.(xlsx|xls|csv)$/i, "");
      setPendingImportName(defaultName);
      setPendingImport({ parsedPlayers });
    } catch(e) {
      setImportNewError("Import failed: " + (e.response?.data?.error || e.message));
    } finally { setImportingNew(false); }
  };

  const confirmImportNew = async () => {
    if (!pendingImportName.trim() || !pendingImport) return;
    setImportingNew(true);
    try {
      const res = await api.post("/player-lists/import-new", { name: pendingImportName.trim(), parsed_players: pendingImport.parsedPlayers });
      setPendingImport(null); setPendingImportName("");
      await loadLists();
      openList(res.data.list);
      showToast(`Created "${res.data.list.name}" with ${res.data.added} players`);
    } catch(e) {
      setImportNewError("Import failed: " + (e.response?.data?.error || e.message));
    } finally { setImportingNew(false); }
  };

  const pickImportFile = () => fileInputRef.current?.click();

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImportError(""); setImporting(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "" });
      if (!rows.length) { setImportError("That file has no rows to import."); return; }
      const parseRes = await api.post("/players/excel-parse", { rows });
      const parsedPlayers = parseRes.data.players || [];
      if (!parsedPlayers.length) { setImportError("Couldn't find any players in that spreadsheet."); return; }
      const res = await api.post(`/player-lists/${selected.id}/import`, { parsed_players: parsedPlayers });
      setEntries(prev => [...prev, ...res.data.entries].sort((a,b) => (a.name||"").localeCompare(b.name||"")));
      showToast(`Added ${res.data.added} from spreadsheet`);
      loadLists();
    } catch(e) {
      setImportError("Import failed: " + (e.response?.data?.error || e.message));
    } finally { setImporting(false); }
  };

  const saveCurrentGroup = async () => {
    if (!saveGroupName.trim()) return;
    setSavingGroup(true);
    try {
      const res = await api.post("/player-lists/save-current-group", { name: saveGroupName.trim() });
      setSaveGroupName("");
      await loadLists();
      openList(res.data.list);
      showToast(`Saved "${res.data.list.name}" with ${res.data.saved} players`);
    } catch(e) { showToast(e.response?.data?.error || "Couldn't save current group"); }
    finally { setSavingGroup(false); }
  };

  const recallList = () => setConfirmLoadGroup(true);
  const confirmRecall = async () => {
    setConfirmLoadGroup(false);
    setRecalling(true);
    try {
      const res = await api.post(`/player-lists/${selected.id}/recall`);
      const { playerIds } = res.data;
      // Mike's explicit ask: land on Players showing ONLY this group, not
      // the whole roster with these ticked somewhere inside it -- an
      // imported Excel group can easily include names not on the roster
      // yet, so hunting them out of everyone else defeats the point.
      sessionStorage.setItem("gsm_group_filter", JSON.stringify({ listName: selected.name, playerIds }));
      window.location.href = "/players";
      return;
    } catch(e) { showToast("Couldn't recall list"); }
    finally { setRecalling(false); }
  };

  return (
    <Layout>
      {toast && <div style={{position:"fixed",top:"20px",left:"50%",transform:"translateX(-50%)",backgroundColor:"#dcfce7",color:"#14532d",padding:"10px 22px",borderRadius:"8px",fontWeight:"700",fontSize:"14px",zIndex:9999,boxShadow:"0 4px 12px rgba(0,0,0,0.2)"}}>{toast}</div>}
      <div className="flex h-full">
        {/* Left: list of lists */}
        <div style={{width:"280px",borderRight:"1px solid #e5e7eb",background:"white",display:"flex",flexDirection:"column"}}>
          <div style={{padding:"14px",borderBottom:"1px solid #e5e7eb"}}>
            <div style={{fontSize:"13px",fontWeight:700,color:"#14532d",marginBottom:"10px"}}>📋 Player Lists</div>

            <button onClick={pickImportNewFile} disabled={importingNew}
              style={{width:"100%",background:"#4338ca",color:"white",border:"none",borderRadius:6,padding:"10px",fontSize:13,fontWeight:700,cursor:"pointer",marginBottom:"8px",opacity:importingNew?0.6:1}}>
              {importingNew ? "Reading…" : "📊 Import Group from Excel"}
            </button>
            <input ref={fileInputNewRef} type="file" accept=".xls,.xlsx,.csv" style={{display:"none"}} onChange={handleImportNewFile} />
            {importNewError && <div style={{color:"#b91c1c",fontSize:11,marginBottom:"8px"}}>{importNewError}</div>}

            <div style={{display:"flex",gap:"6px",marginBottom:"8px"}}>
              <input value={saveGroupName} onChange={e => setSaveGroupName(e.target.value)} onKeyDown={e => e.key === "Enter" && saveCurrentGroup()}
                placeholder="Name for current group…" style={{flex:1,border:"1px solid #d1d5db",borderRadius:6,padding:"6px 8px",fontSize:12}} />
              <button onClick={saveCurrentGroup} disabled={savingGroup || !saveGroupName.trim()}
                title="Saves whoever is currently ticked Playing in your active competition as a new list"
                style={{background:"#1d4ed8",color:"white",border:"none",borderRadius:6,padding:"6px 10px",fontSize:12,fontWeight:700,cursor:"pointer",opacity:(savingGroup||!saveGroupName.trim())?0.6:1,whiteSpace:"nowrap"}}>
                💾 Save Group
              </button>
            </div>

            <div style={{fontSize:11,color:"#9ca3af",margin:"10px 0 6px",fontWeight:700}}>Or start with an empty list:</div>
            <div style={{display:"flex",gap:"6px"}}>
              <input value={newListName} onChange={e => setNewListName(e.target.value)} onKeyDown={e => e.key === "Enter" && createList()}
                placeholder="New list name…" style={{flex:1,border:"1px solid #d1d5db",borderRadius:6,padding:"6px 8px",fontSize:12}} />
              <button onClick={createList} style={{background:"#166534",color:"white",border:"none",borderRadius:6,padding:"6px 10px",fontSize:12,fontWeight:700,cursor:"pointer"}}>+</button>
            </div>
          </div>
          <div style={{flex:1,overflowY:"auto"}}>
            {loading ? <div style={{padding:"14px",color:"#9ca3af",fontSize:12}}>Loading…</div> :
             lists.length === 0 ? <div style={{padding:"14px",color:"#9ca3af",fontSize:12}}>No lists yet — create one above.</div> :
             lists.map(l => (
              <div key={l.id} onClick={() => openList(l)}
                style={{padding:"12px 14px",cursor:"pointer",borderBottom:"1px solid #f3f4f6",background: selected?.id === l.id ? "#f0fdf4" : "white"}}>
                <div style={{fontSize:13,fontWeight:600,color:"#111827"}}>{l.name}</div>
                <div style={{fontSize:11,color:"#9ca3af"}}>{l.entry_count} {l.entry_count === 1 ? "person" : "people"}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Main: selected list */}
        <div style={{flex:1,padding:"20px",overflowY:"auto"}}>
          {!selected ? (
            <div style={{color:"#9ca3af",fontSize:13}}>Select a list on the left, or create a new one.</div>
          ) : (
            <>
              <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"16px",flexWrap:"wrap"}}>
                {renaming ? (
                  <>
                    <input value={renameValue} onChange={e => setRenameValue(e.target.value)} onKeyDown={e => e.key === "Enter" && saveRename()}
                      autoFocus style={{fontSize:18,fontWeight:700,border:"1px solid #d1d5db",borderRadius:6,padding:"4px 8px"}} />
                    <button onClick={saveRename} style={{background:"#166534",color:"white",border:"none",borderRadius:6,padding:"6px 12px",fontSize:12,fontWeight:700,cursor:"pointer"}}>Save</button>
                  </>
                ) : (
                  <>
                    <div style={{fontSize:18,fontWeight:700,color:"#14532d"}}>{selected.name}</div>
                    <button onClick={startRename} style={{background:"none",border:"1px solid #d1d5db",borderRadius:6,padding:"4px 10px",fontSize:11,cursor:"pointer",color:"#6b7280"}}>Rename</button>
                  </>
                )}
                <button onClick={() => deleteList(selected)} style={{marginLeft:"auto",background:"none",border:"1px solid #fecaca",borderRadius:6,padding:"6px 12px",fontSize:12,fontWeight:700,color:"#b91c1c",cursor:"pointer"}}>🗑️ Delete List</button>
              </div>

              <div style={{display:"flex",gap:"8px",marginBottom:"16px",flexWrap:"wrap",alignItems:"center"}}>
                <input value={newEntryName} onChange={e => setNewEntryName(e.target.value)} onKeyDown={e => e.key === "Enter" && addEntry()}
                  placeholder="Full name" style={{border:"1px solid #d1d5db",borderRadius:6,padding:"8px 10px",fontSize:13,width:"220px"}} />
                <input value={newEntryHcp} onChange={e => setNewEntryHcp(e.target.value)} onKeyDown={e => e.key === "Enter" && addEntry()}
                  placeholder="Hcp" type="number" style={{border:"1px solid #d1d5db",borderRadius:6,padding:"8px 10px",fontSize:13,width:"70px"}} />
                <button onClick={addEntry} style={{background:"#166534",color:"white",border:"none",borderRadius:6,padding:"8px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>Add</button>
                <button onClick={pickImportFile} disabled={importing}
                  style={{background:"#4338ca",color:"white",border:"none",borderRadius:6,padding:"8px 14px",fontSize:12,fontWeight:700,cursor:"pointer",opacity:importing?0.6:1}}>
                  {importing ? "Reading…" : "📊 Import from Excel"}
                </button>
                <input ref={fileInputRef} type="file" accept=".xls,.xlsx,.csv" style={{display:"none"}} onChange={handleImportFile} />
                <button onClick={recallList} disabled={recalling || entries.length === 0}
                  title="Adds everyone in this list into your live Players roster, ready to tick Playing"
                  style={{background:"#1d4ed8",color:"white",border:"none",borderRadius:6,padding:"8px 14px",fontSize:12,fontWeight:700,cursor:"pointer",opacity:(recalling||entries.length===0)?0.6:1}}>
                  {recalling ? "Adding…" : "📂 Load Group"}
                </button>
              </div>
              {importError && <div style={{color:"#b91c1c",fontSize:12,marginBottom:12}}>{importError}</div>}

              {loadingEntries ? (
                <div style={{color:"#9ca3af",fontSize:13}}>Loading…</div>
              ) : entries.length === 0 ? (
                <div style={{color:"#9ca3af",fontSize:13}}>No one in this list yet — add a name above or import a spreadsheet.</div>
              ) : (
                <table style={{width:"100%",maxWidth:"500px",borderCollapse:"collapse"}}>
                  <thead>
                    <tr style={{background:"#f3f4f6"}}>
                      <th style={{textAlign:"left",padding:"8px 10px",fontSize:12,color:"#6b7280"}}>Name</th>
                      <th style={{textAlign:"center",padding:"8px 10px",fontSize:12,color:"#6b7280"}}>Hcp</th>
                      <th style={{textAlign:"center",padding:"8px 10px",fontSize:12,color:"#6b7280"}}>On Roster?</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map(en => (
                      <tr key={en.id} style={{borderBottom:"1px solid #f3f4f6"}}>
                        <td style={{padding:"8px 10px",fontSize:13}}>{en.name}</td>
                        <td style={{padding:"8px 10px",fontSize:13,textAlign:"center"}}>{en.handicap}</td>
                        <td style={{padding:"8px 10px",fontSize:12,textAlign:"center",color: en.player_id ? "#166534" : "#9ca3af"}}>{en.player_id ? "Yes" : "—"}</td>
                        <td style={{padding:"8px 10px",textAlign:"center"}}>
                          <button onClick={() => removeEntry(en)} style={{background:"none",border:"none",color:"#b91c1c",cursor:"pointer",fontSize:13}}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>

      {confirmDeleteList && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"400px",width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"#b91c1c",marginBottom:"8px"}}>⚠️ Delete "{confirmDeleteList.name}"?</div>
            <div style={{fontSize:"13px",color:"#374151",marginBottom:"16px"}}>
              This removes the list and all its entries. It does NOT touch anyone on the live Players roster.
            </div>
            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
              <button onClick={() => setConfirmDeleteList(null)}
                style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                Cancel
              </button>
              <button onClick={confirmDelete}
                style={{background:"#b91c1c",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer"}}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmLoadGroup && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"440px",width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"#1e40af",marginBottom:"8px"}}>📂 Load "{selected?.name}"?</div>
            <div style={{fontSize:"13px",color:"#374151",marginBottom:"16px"}}>
              Adds everyone in this list ({entries.length} people) into your live Players roster and ticks them Playing on today's active event (if you have one set up), then takes you straight to Players showing just this group. Anyone already on the roster with a matching name has their handicap updated to match this list; everyone else is added as a new active player. This never touches groups, teams, or tee times.
            </div>
            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
              <button onClick={() => setConfirmLoadGroup(false)}
                style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                Cancel
              </button>
              <button onClick={confirmRecall}
                style={{background:"#1e40af",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer"}}>
                Load Group
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingImport && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"420px",width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"#14532d",marginBottom:"8px"}}>Found {pendingImport.parsedPlayers.length} players</div>
            <div style={{fontSize:"13px",color:"#374151",marginBottom:"12px"}}>Name this group:</div>
            <input value={pendingImportName} onChange={e => setPendingImportName(e.target.value)} onKeyDown={e => e.key === "Enter" && confirmImportNew()}
              autoFocus style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"10px 12px",fontSize:14,boxSizing:"border-box",marginBottom:"16px"}} />
            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
              <button onClick={() => { setPendingImport(null); setPendingImportName(""); }}
                style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                Cancel
              </button>
              <button onClick={confirmImportNew} disabled={!pendingImportName.trim() || importingNew}
                style={{background:"#166534",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:(!pendingImportName.trim()||importingNew)?0.6:1}}>
                {importingNew ? "Creating…" : "Create Group"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
